import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, Label, Icon, IconWrapper } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Context from './Context';
import themeStyles from './AddToBuildButton.styles';
import getDropdownOptionsData, { optionsKeys } from '../../../utils/getDropdownOptionsData';
import useDropdownOptions from '../../../hooks/useDropdownOptions';
import { pushGtmAccessorySelect } from '../service/utils';

const CheckmarkWrapper = themeStyles.apply(IconWrapper, 'CheckmarkWrapper');
const Container = themeStyles.apply(Box, 'Container');
const CompareLabel = themeStyles.apply(Label, 'CompareLabel');
const CheckboxInput = styled('input').attrs({ type: 'checkbox', tabIndex: '-1', autoComplete: 'off' })(
  themeStyles.get('CheckboxInput'),
);
const ToggleWrapper = styled('label')(themeStyles.get('ToggleWrapper'));

// lifted plus and cross toggle from jss components
const StyledCheckmark = styled('span')(
  ({ disabled, theme }) => `
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;

  &::before {
    content: '';
    position: absolute;
    display: block;
    background-color: ${disabled ? theme.colors.grey[0] : theme.colors.red};
    transition: 0.4s all;
    bottom: 4px;
    left: 35%;
    width: 5%;
    height: 55%;
    border: solid ${disabled ? theme.colors.grey[0] : theme.colors.red};
    border-width: 0.1px;
    -ms-transform: rotate(180deg);
    transform: rotate(180deg);
  }

  &::after {
    content: '';
    position: absolute;
    display: block;
    background-color: ${disabled ? theme.colors.grey[0] : theme.colors.red};
    transition: 0.4s all;
    bottom: 4px;
    left: 35%;
    width: 5%;
    height: 55%;
    border: solid ${disabled ? theme.colors.grey[0] : theme.colors.red};
    border-width: 0.1px;
    -ms-transform: rotate(90deg);
    transform: rotate(90deg);
  }

  ${ToggleWrapper}:hover & {
    border-color: #007dba;
  }

  ${CheckboxInput}:checked + &::before {
    display: block;
    transition: 0.4s all;
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }

  ${CheckboxInput}:checked + &::after {
    display: block;
    transition: 0.4s all;
    -ms-transform: rotate(135deg);
    transform: rotate(135deg);
  }

  ${CheckboxInput}:checked + & {
  }

  ${CheckboxInput}:disabled + & {
    border-color: #c7c7c7;
  }
`,
);

const AddToBuildButton = ({ isDisabled, accessoryName }) => {
  const { dropdownOptions } = useDropdownOptions();
  const [yearData, modelData, transmissionData] = getDropdownOptionsData(dropdownOptions, [
    optionsKeys.year,
    optionsKeys.model,
    optionsKeys.transmission,
  ]);

  const modelName = modelData?.text;
  const modelYear = yearData?.value;
  const modelTransmission = transmissionData?.value;

  const { isOnBuild, toggleBuild } = useContext(Context);

  const { t } = useTranslation();
  const compareIconLabel = isOnBuild
    ? t('Sub-apps.PriceAndFinance.removeFromBuildButton')
    : t('Sub-apps.PriceAndFinance.addToBuildButton');

  const preventContainerClickPropagation = event => {
    event?.stopPropagation();
  };

  const handleCheckboxChange = event => {
    pushGtmAccessorySelect(accessoryName, `${modelYear}: ${modelTransmission}: ${modelName}`);
    toggleBuild(event);
  };

  return (
    <>
      <CheckmarkWrapper isOnBuild={isOnBuild} data-testid="cy-build-checkmark">
        <Icon name="checkmark" iconColor="white" />
      </CheckmarkWrapper>
      <Container onClick={!isDisabled ? preventContainerClickPropagation : undefined}>
        <ToggleWrapper data-testid="cy-add-to-build-button">
          <CheckboxInput
            containerSize="small"
            checked={isOnBuild}
            onChange={!isDisabled ? handleCheckboxChange : undefined}
            disabled={isDisabled}
          />
          <StyledCheckmark disabled={isDisabled} data-testid="cy-build-toggle" />
          <CompareLabel disabled={isDisabled} data-testid="cy-build-label">
            {compareIconLabel}
          </CompareLabel>
        </ToggleWrapper>
      </Container>
    </>
  );
};

AddToBuildButton.propTypes = {
  isDisabled: PropTypes.bool,
  accessoryName: PropTypes.string,
};

export default AddToBuildButton;
